"use client";

import {NeoDatePicker, NeoSelect} from "@/components/forms";
import {useNeoContext} from "@/context/neo/neo.context";
import {ExpenseService} from "@/service/expense.service";
import {SelectItem} from "@nextui-org/react";
import React, {useEffect, useState} from "react";
import toast from "react-hot-toast";
import {FaCalendarAlt, FaLayerGroup} from "react-icons/fa";
import {MdDirectionsBus} from "react-icons/md";

interface DateRange {
  from: Date | null | undefined;
  to: Date | null | undefined;
}

interface FilterData {
  dateRange?: DateRange;
  vehicles?: string[];
  group?: string;
  selectedFilterType?: string;
}

interface ExpenseReportsFilterProps {
  defaultFilterType?: string;
  isGroupSelectboxVisible?: boolean;
  onFilterChange: (data: FilterData) => void;
  defaultselectedDateRange?: DateRange | null;
  defaultselectedVehicleIds?: string[];
}

export const ExpenseReportsFilter: React.FC<ExpenseReportsFilterProps> = ({
  defaultFilterType = "1",
  isGroupSelectboxVisible = false,
  onFilterChange,
  defaultselectedDateRange = null,
  defaultselectedVehicleIds = []
}) => {
  const {filterContext} = useNeoContext();
  const [loginUserId, setLoginUserId] = useState("");
  const [showDateRange, setShowDateRange] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedFilterType, setSelectedFilterType] =
    useState(defaultFilterType);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(
    null
  );
  const [vehicles, setvehicles] = useState<string[]>([]);
  const [selectedVehicleIds, setVehicleIds] = useState<string[]>(
    defaultselectedVehicleIds
  );
  const [selecteGroupId, setGroupId] = useState<string>("");
  const mainFilter = [
    {label: "Today", value: "1"},
    {label: "Yesterday", value: "2"},
    {label: "Current Week", value: "3"},
    {label: "Last Week", value: "4"},
    {label: "Current Month", value: "5"},
    {label: "Last Month", value: "6"},
    {label: "Range", value: "7"}
  ];

  const groups = [
    {label: "Vehicle", value: "1"},
    {label: "Date", value: "2"},
    {label: "Expense", value: "3"}
  ];

  useEffect(() => {
    if (filterContext?.client?.institutionUserId) {
      setLoginUserId(filterContext.client.institutionUserId);
    }
  }, [filterContext?.client?.institutionUserId]);

  useEffect(() => {
    if (loginUserId) {
      fetchVehicleDatas();
    }
  }, [loginUserId]);

  const fetchVehicleDatas = async () => {
    try {
      const requestData = {
        filters: {
          branch: [loginUserId]
        }
      };

      const response = await ExpenseService.getvehicles(requestData);
      if (
        response?.data?.status === "success" &&
        response?.data?.data?.reportData
      ) {
        const datas = response.data.data?.reportData;
        setvehicles(datas);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  };

  useEffect(() => {
    handleFromToFilterData(defaultFilterType);
  }, [defaultFilterType]);

  const handleFromToFilterData = (filterType: string) => {
    const currentDate = new Date();

    if (filterType === "1") {
      const from = new Date(currentDate);
      const to = new Date(currentDate);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      updateRange({from, to});
    } else if (filterType === "2") {
      const from = new Date(currentDate);
      from.setDate(currentDate.getDate() - 1);
      const to = new Date(currentDate);
      to.setDate(currentDate.getDate() - 1);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      updateRange({from, to});
    } else if (filterType === "3" || filterType === "4") {
      const dayOfWeek = currentDate.getDay();
      const diffToMonday = (dayOfWeek + 6) % 7;
      const weekStart = new Date(currentDate);
      weekStart.setDate(currentDate.getDate() - diffToMonday);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      if (filterType === "3") {
        const to = new Date(currentDate);
        to.setHours(23, 59, 59, 999);
        updateRange({from: weekStart, to});
      } else {
        const lastWeekFrom = new Date(weekStart);
        lastWeekFrom.setDate(weekStart.getDate() - 7);
        const lastWeekTo = new Date(weekEnd);
        lastWeekTo.setDate(weekEnd.getDate() - 7);
        updateRange({from: lastWeekFrom, to: lastWeekTo});
      }
    } else if (filterType === "5") {
      const from = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const to = new Date(currentDate);
      to.setHours(23, 59, 59, 999);
      updateRange({from, to});
    } else if (filterType === "6") {
      const from = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1
      );
      const to = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      to.setHours(23, 59, 59, 999);
      updateRange({from, to});
    } else if (filterType === "7") {
      const from = defaultselectedDateRange?.from
        ? new Date(defaultselectedDateRange?.from)
        : new Date(currentDate);
      const to = defaultselectedDateRange?.to
        ? new Date(defaultselectedDateRange?.to)
        : new Date(currentDate);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      setShowDateRange(true);
      updateRange({from, to});
      return;
    }
    setShowDateRange(false);
  };

  const updateRange = (range: DateRange) => {
    setSelectedDateRange(range);
  };

  const handleFilterTypeChange = (value: string) => {
    setSelectedFilterType(value);
    handleFromToFilterData(value);
  };

  const handleDateChange = (range: DateRange | Date | Date[] | null): void => {
    if (
      range &&
      typeof range === "object" &&
      "from" in range &&
      "to" in range
    ) {
      setSelectedDateRange(range as DateRange);
    }
  };

  const handleGroupChange = (value: string) => {
    setGroupId(value);
  };

  useEffect(() => {
    const requestdata: any = {
      vehicleIds: selectedVehicleIds,
      groupId: selecteGroupId,
      range: selectedDateRange,
      selectedFilterType: selectedFilterType
    };
    if (selectedVehicleIds && selectedDateRange) {
      onFilterChange(requestdata);
    }
  }, [selectedDateRange, selecteGroupId, selectedVehicleIds]);

  return (
    <div className="flex gap-2">
      <div className="h-10 w-40">
        <NeoSelect
          selectedKeys={[selectedFilterType]}
          startContent={<FaCalendarAlt className="text-[#1e2a44]" />}
          onSelectionChange={keys =>
            handleFilterTypeChange(Array.from(keys)[0] as string)
          }
          size="md"
          placeholder="Select Date Type"
        >
          {mainFilter.map(item => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </NeoSelect>
      </div>
      {showDateRange && (
        <div className="">
          <NeoDatePicker
            //label="Date Range"
            type="range"
            handleDateChange={handleDateChange}
            rangedefaultValue={selectedDateRange}
          />
        </div>
      )}
      <div className="h-10 w-60">
        <NeoSelect
          selectionMode="multiple"
          size="md"
          placeholder="Select Vehicles"
          selectedKeys={new Set(selectedVehicleIds)}
          onSelectionChange={keys => {
            const stringKeys = Array.from(keys).map(String);
            setVehicleIds(stringKeys);
          }}
          startContent={<MdDirectionsBus className="h-6 w-6 text-[#1e2a44]" />}
        >
          {vehicles.map((item: any) => (
            <SelectItem key={item.id} value={item.id.toString()}>
              {item.regNo}
            </SelectItem>
          ))}
        </NeoSelect>
      </div>
      {isGroupSelectboxVisible && (
        <div className="h-10 w-40">
          <NeoSelect
            size="md"
            placeholder="Group by"
            selectedKeys={
              selecteGroupId ? new Set([selecteGroupId]) : new Set()
            }
            onSelectionChange={keys => {
              const first = Array.isArray(keys)
                ? keys[0]
                : Array.from(keys as Set<unknown>)[0];
              const value: any =
                first !== undefined && first !== null ? String(first) : null;
              handleGroupChange(value);
            }}
            startContent={<FaLayerGroup className="h-4 w-4 text-[#1e2a44]" />}
          >
            {groups.map(item => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </NeoSelect>
        </div>
      )}
    </div>
  );
};
